'use client'

import { memo, useState, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

export type MarketingDemoTab = {
  id: string
  label: string
  title: string
  body: string
  bullets?: readonly string[]
  /** Optional rich media under the copy (video / mock) */
  media?: ReactNode
}

export type MarketingDemoTabsProps = {
  label?: string
  title: string
  description?: string
  tabs: readonly MarketingDemoTab[]
  className?: string
}

const EASE = [0.16, 1, 0.3, 1] as const

/**
 * baby: Webflow demo-tabs pattern. How to / Demo / Use cases without IX2.
 */
export const MarketingDemoTabs = memo(function MarketingDemoTabs({
  label = 'GUIDE',
  title,
  description,
  tabs,
  className,
}: MarketingDemoTabsProps) {
  const reduced = Boolean(useReducedMotion())
  const [activeId, setActiveId] = useState(tabs[0]?.id ?? '')
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0]

  if (!active || tabs.length === 0) {
    return null
  }

  return (
    <section
      className={cn('mx-auto max-w-5xl px-6', className)}
      data-section="demo-tabs"
    >
      <div className="mb-8 text-center">
        <div className="ninxl-8bit-label mb-2 tracking-[0.2em] text-[color:var(--ninxl-brand-teal)]">
          {label}
        </div>
        <h2 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-base md:text-lg">
          {title}
        </h2>
        {description ? (
          <p className="ninxl-8bit-body mx-auto max-w-2xl">{description}</p>
        ) : null}
      </div>

      <div
        role="tablist"
        aria-label={title}
        className="marketing-demo-tabs-list mb-6 flex flex-wrap justify-center gap-2"
      >
        {tabs.map((tab) => {
          const selected = tab.id === active.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              id={`demo-tab-${tab.id}`}
              aria-controls={`demo-panel-${tab.id}`}
              className={cn(
                'ninxl-pixel-btn px-4 py-2 text-sm',
                selected && 'ninxl-pixel-btn-primary',
              )}
              onClick={() => setActiveId(tab.id)}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active.id}
          role="tabpanel"
          id={`demo-panel-${active.id}`}
          aria-labelledby={`demo-tab-${active.id}`}
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? undefined : { opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="ninxl-pixel-border bg-[color:var(--ninxl-8bit-panel)] p-6 md:p-8"
        >
          <h3 className="ninxl-8bit-heading ninxl-theme-text mb-3 text-sm md:text-base">
            {active.title}
          </h3>
          <p className="ninxl-8bit-body mb-4 max-w-2xl text-sm md:text-base">
            {active.body}
          </p>
          {active.bullets && active.bullets.length > 0 ? (
            <ul className="mb-5 space-y-2">
              {active.bullets.map((bullet) => (
                <li
                  key={bullet}
                  className="ninxl-8bit-body flex gap-2 text-sm before:mt-2 before:h-1.5 before:w-1.5 before:shrink-0 before:rounded-full before:bg-[color:var(--ninxl-brand-teal)] before:content-['']"
                >
                  {bullet}
                </li>
              ))}
            </ul>
          ) : null}
          {active.media ? (
            <div className="marketing-demo-tabs-media mt-2 overflow-hidden border border-[color:var(--ninxl-8bit-border)]/40">
              {active.media}
            </div>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </section>
  )
})
